import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import type { AdyenCheckoutError, PaymentCompletedData, PaymentFailedData } from '@adyen/adyen-web';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import { RedcatApiHandler } from 'polygon-utils';
import type { OnlinePaymentSessionResponseDto } from '../../../shared/types/OnlinePayments';
import { useRedcatPayment } from '../hooks/useRedcatPayment';
import { RedcatPaymentCardFormSkeleton } from './RedcatPaymentCardFormSkeleton';
import { Show } from '../../../components/atoms/Show';
import { paymentErrorTestingEnabled } from '../utils/paymentErrorTestingEnabled';

interface RedcatPaymentCardFormProps {
  readonly amount: number;
  readonly storeId: string;
  readonly environment: 'live' | 'test';
  readonly apiHandlerReady: boolean;
  readonly iframeTextColor?: string;
  readonly memberRef?: { id: number; number: string };
  readonly disabled?: boolean;
  readonly loyalty?: Record<string, unknown>;
  readonly onCompleted: (data: PaymentCompletedData, reference: string | undefined) => void;
  readonly onFailed: (data: PaymentFailedData) => void;
  readonly onError: (error: AdyenCheckoutError) => void;
}

export const RedcatPaymentCardForm: FC<RedcatPaymentCardFormProps> = ({
  amount,
  storeId,
  environment,
  apiHandlerReady,
  iframeTextColor,
  memberRef,
  loyalty,
  disabled = false,
  onCompleted,
  onFailed,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // A 3DS2 challenge renders inside our container. It needs pointer events even while the
  // parent has disabled the form (checkoutInProgress), so track it and override `disabled`.
  const [challengeActive, setChallengeActive] = useState(false);
  // Forces the mount effect to rerun on failure. Context state (idempotencyKey,
  // pollingReference, recaptchaToken) may already be null at this point, so it can't be
  // relied on to change and retrigger the effect.
  const [attempt, setAttempt] = useState(0);
  // Adyen mounts into containerRef imperatively, invisible to React's render cycle,
  // so this tracks readiness ourselves to know when to swap the skeleton out.
  const [cardMounted, setCardMounted] = useState(false);
  const {
    registerCardComponent,
    unregisterCardComponent,
    setCardValid,
    recaptchaToken,
    setRecaptchaToken,
    requireRecaptcha,
    recaptchaSiteKey,
    pollingReference,
    idempotencyKey,
    setIdempotencyKey,
    resetPaymentAttempt,
  } = useRedcatPayment();

  useEffect(() => {
    return () => {
      if (requireRecaptcha) resetPaymentAttempt();
    };
  }, []);

  useEffect(() => {
    if (
      !containerRef.current ||
      !apiHandlerReady ||
      (requireRecaptcha && !recaptchaToken) ||
      pollingReference !== null
    )
      return;

    let cardComponent: InstanceType<typeof Card> | undefined;
    let cancelled = false;

    const init = async () => {
      try {
        const returnUrl = window.location.href;
        const response = await RedcatApiHandler.fetch({
          method: 'POST',
          path: '/api/v2/online-payments/sessions',
          headers: idempotencyKey ? { 'X-REDCAT-IDEMPOTENCY-KEY': idempotencyKey } : undefined,
          body: {
            amount,
            store_id: storeId,
            return_url: returnUrl,
            channel: 'web',
            ...(memberRef && { member_ref: memberRef }),
            ...(loyalty && { loyalty }),
            ...(requireRecaptcha && recaptchaToken && { 'g-recaptcha-response': recaptchaToken }),
          },
        });
        const session = response.data as OnlinePaymentSessionResponseDto;
        if (session.idempotency_key) setIdempotencyKey(session.idempotency_key);
        if (cancelled) return;
        const checkout = await AdyenCheckout({
          environment,
          clientKey: session.client_key,
          analytics: { enabled: false },
          showPayButton: false,
          session: {
            id: session.session_id,
            sessionData: session.session_token,
          },
          countryCode: session.country_code,
          onActionHandled: ({ componentType }) => {
            // Adyen reports the visible step-up as "3DS2Challenge" (fingerprint is silent).
            if (componentType === '3DS2Challenge') setChallengeActive(true);
          },
          onPaymentCompleted: data => {
            setChallengeActive(false);
            onCompleted(data, session.reference);
          },
          onPaymentFailed: data => {
            setChallengeActive(false);
            setAttempt(a => a + 1);
            onFailed(data as PaymentFailedData);
          },
          onError: error => {
            setChallengeActive(false);
            setAttempt(a => a + 1);
            onError(error);
          },
        });

        if (cancelled || !containerRef.current) return;
        const showHolderName = paymentErrorTestingEnabled();
        cardComponent = new Card(checkout, {
          enableStoreDetails: !!memberRef,
          hasHolderName: showHolderName,
          onChange: state => setCardValid(state.isValid),
          onLoad: ({ iframesLoaded }) => {
            if (iframesLoaded) setCardMounted(true);
          },
          styles: {
            base: { color: iframeTextColor, background: 'transparent' },
            placeholder: { color: iframeTextColor, background: 'transparent' },
            validated: { color: iframeTextColor, background: 'transparent' },
            error: { color: iframeTextColor, background: 'transparent' },
          },
        });
        cardComponent.mount(containerRef.current);
        registerCardComponent(cardComponent);
      } catch (error) {
        if (cancelled) return;
        if (requireRecaptcha) setRecaptchaToken(null);
        onError(error as AdyenCheckoutError);
      }
    };

    init();

    return () => {
      cancelled = true;
      cardComponent?.unmount();
      unregisterCardComponent();
      setCardValid(false);
      setCardMounted(false);
      if (!pollingReference) setIdempotencyKey(null);
    };
  }, [apiHandlerReady, recaptchaToken, pollingReference, attempt]);

  // Keep the form interactive while a 3DS2 challenge is up, even if the parent disabled it.
  const effectiveDisabled = disabled && !challengeActive;

  const recaptchaActive = !!requireRecaptcha && !recaptchaToken;
  const showRecaptcha = recaptchaActive && !!recaptchaSiteKey;
  const showSkeleton = !cardMounted && !recaptchaActive;

  return (
    <div
      data-testid="redcat-payment-card-form"
      aria-disabled={effectiveDisabled}
      style={{
        pointerEvents: effectiveDisabled ? 'none' : 'auto',
        opacity: effectiveDisabled ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <Show when={showRecaptcha}>
        <div style={styles.recaptchaContainer}>
          <ReCAPTCHA
            sitekey={recaptchaSiteKey!}
            onChange={token => setRecaptchaToken(token)}
            onErrored={() => setRecaptchaToken(null)}
          />
        </div>
      </Show>
      <Show when={showSkeleton}>
        <RedcatPaymentCardFormSkeleton showStoreDetails={!!memberRef} />
      </Show>
      <div ref={containerRef} style={{ display: cardMounted ? 'block' : 'none' }} />
    </div>
  );
};

const styles: Styles = {
  recaptchaContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 16,
  },
};
